import { Injectable } from '@angular/core';
import { WatchQueryFetchPolicy } from '@apollo/client/core';
import {
	ClientLocalization,
	ClientLocalizationRequest,
	CmLocalizationItem,
	LocType,
	PortalType,
} from '@ats/api/cm';
import { CmLocalizationService, ERROR_GUIDS } from '@ats/cm/core';
import { BoolUtil } from '@ats/utility';
import { CultureService } from '@cm/core/culture';
import { SettingsService } from '@cm/core/settings';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { catchError, combineLatestWith, distinctUntilChanged, take } from 'rxjs/operators';
import { SYSTEM_LOC_KEYS, systemLocs } from '../data';
import { ErrorLocTypeGuard } from '../data/errors.locs';
import { LocDef, Localization, LocalizationVM, mapClientLocs } from '../models';
import { errorLocMap } from '../models/error-maps';
import { CLIENT_LOCALIZATIONS } from './client-localization.query';
import { LocalizationCoreService } from './localization-core.service';

@Injectable({
	providedIn: 'root',
})
export class LocalizationService {
	public readonly Keys = SYSTEM_LOC_KEYS;
	loaded$: Observable<boolean>;
	readonly booleanDisplay$: Observable<(value?: boolean | string) => string>;
	private readonly localizations = new BehaviorSubject<Record<string, Localization>>({});
	private readonly localizations$ = this.localizations.asObservable();

	constructor(
		private readonly apollo: Apollo,
		private readonly settings: SettingsService,
		private readonly legacyService: CmLocalizationService,
		private readonly core: LocalizationCoreService,
		private readonly cultureService: CultureService
	) {
		this.loaded$ = this.localizations$.pipe(map(locs => Object.keys(locs).length > 0));
		this.load(systemLocs);
		this.booleanDisplay$ = combineLatest({
			[BoolUtil.Positive]: this.findValue$(this.Keys.GlobalValues.CMSystemGlobalTrue),
			[BoolUtil.Negative]: this.findValue$(this.Keys.GlobalValues.CMSystemGlobalFalse),
			[BoolUtil.Unavailable]: this.findValue$(this.Keys.GlobalValues.CMSystemGlobalNA),
		}).pipe(
			map(
				displayStrings => (value?: boolean | string) =>
					displayStrings[BoolUtil.format(value, false)]
			)
		);
	}

	/**
	 * @deprecated - This is only for legacy support in other places
	 */
	getLoc(name: string): Localization | undefined {
		return this.localizations.getValue()[name] || undefined;
	}

	/**
	 * @deprecated - This is only for legacy support in other places
	 */
	getPartialLoc(partialName: string): Localization | undefined {
		const name = this.getFromParitalName(partialName);
		return name ? this.localizations.getValue()[name] || undefined : undefined;
	}

	/**
	 * @deprecated - This is only for legacy support in other places
	 */
	getFromParitalName(partialName: string): string | undefined {
		return Object.keys(this.localizations.getValue()).find(key => key.includes(partialName));
	}

	find$<T extends Record<string, string>>(
		name: string[]
	): Observable<{ [K in keyof T]?: LocalizationVM | string }>;
	find$(name: string): Observable<LocalizationVM | null>;
	find$<T extends string>(
		name: string | string[]
	): Observable<LocalizationVM | { [K in keyof T]?: LocalizationVM | string } | null> {
		if (typeof name === 'string') {
			return this.core.localization$(name);
		} else {
			return this.core.localizationMap$(name).pipe(
				combineLatestWith(this.cultureService.culture$),
				map(([locs]) =>
					Object.values(locs).reduce(
						(acc, loc) => ({
							...acc,
							[loc.name]: loc,
						}),
						{}
					)
				)
			);
		}
	}

	findValue$(name: string): Observable<string> {
		return this.find$(name).pipe(
			switchMap(loc => {
				if (!loc) {
					return this.getLegacyLocValue$(name);
				}
				if (loc.value?.trim() === '') {
					return of(loc.value);
				}
				return of(loc.value || loc.defaultValue || '');
			}),
			distinctUntilChanged()
		);
	}

	findValues$<T extends { [name: string]: any }>(locs: { [K in keyof T]: string }): Observable<{
		[K in keyof T]: string;
	}> {
		const keys = Object.keys(locs) as (keyof T)[];
		let locMap = {};

		keys.forEach(key => {
			if (typeof key === 'string') {
				locMap = {
					...locMap,
					[key]: this.findValue$(locs[key]),
				};
			}
		});
		return combineLatest(locMap);
	}

	/**
	 * This method is for initializing and loading locDefs only.
	 * It will NOT refresh localizations already loaded.
	 */
	load(locs: Record<string, LocDef>, ignorePortal = false): void {
		this.core.load(locs);
	}

	loadRemaingLocs$(locs: Record<string, LocDef>): Observable<Localization[]> {
		// Remove any that already have a client value
		const locArray = Object.entries(locs).reduce(
			(acc, [key, loc]) =>
				!Object.prototype.hasOwnProperty.call(loc, 'value')
					? [...acc, this.mapLocalizationItem([key, loc])]
					: acc,
			[] as CmLocalizationItem[]
		);

		// Stop gap to filter out features in case any are already loaded
		return this.fetchClientLocalizations$(locArray).pipe(
			take(1),
			map(clientLocs => mapClientLocs([clientLocs, locs], this.settings.portal)),
			map(locs => Object.values(locs))
		);
	}

	reloadLoc$(name: string): Observable<Localization | null> {
		return this.getLoc$<Localization>(name).pipe(
			take(1),
			switchMap(loc => {
				if (!loc) {
					return of(null);
				}
				const name = loc.name;
				const type = this.mapLocType(loc);
				return this.settings.cultureCode$.pipe(
					switchMap(culture =>
						this.fetchClientLocalizations$([{ name, type }], culture, 'network-only')
					),
					map(locs => locs[0]),
					// Just get the new value
					map(cloc => ({
						...loc,
						value: cloc.value,
					})),
					tap(cloc => this.updateLocs({ [name]: cloc }))
				);
			})
		);
	}

	getByCulture$(name: string, culture: string, type?: LocType): Observable<string> {
		return this.fetchLocCultureValue$(name, type, culture);
	}

	deleteClientLoc$({ name, cultureName, portal }: Omit<any, 'value'>): Observable<boolean> {
		return this.core.deleteClientLoc$(name, cultureName);
	}

	createOrUpdateClientLoc$({
		name,
		value,
		cultureName,
	}: {
		name: string;
		value: string;
		cultureName?: string;
	}): Observable<LocalizationVM | null | (LocalizationVM | null)[]> {
		return this.core.createOrUpdateClientLoc$({ name, value }, cultureName);
	}

	addLegacyLocalizations(locs: Record<string, Localization>): void {
		this.updateLocs(locs);
	}

	/** @deprecated - Guids are moving to just simply having the GUID as a key.
	 * ErrorLocTypeGuard will verify if a GUID is localized with current locs. */
	findGUIDLocName(guid: string): string | null {
		return ErrorLocTypeGuard(guid) ? guid : ERROR_GUIDS[`GUID_${guid}`] || null;
	}

	transformBooleanDisplay$(value: string | boolean): Observable<string> {
		return this.booleanDisplay$.pipe(map(display => display(value)));
	}

	private getLegacyLocValue$(name: string): Observable<string> {
		return this.legacyService.findLocalization$(name).pipe(
			map(loc => {
				if (!loc || loc.value?.trim() === '') {
					return '';
				} else {
					return loc.value || loc.defaultValue || '';
				}
			})
		);
	}

	private updateLocs(locs: Record<string, Localization>): void {
		this.localizations.next({
			...this.localizations.getValue(),
			...locs,
		});
	}

	private getLoc$<R extends Localization | string>(
		key: string,
		mapToValue = false
	): Observable<R | null> {
		key = errorLocMap[key] || key;
		return this.localizations$.pipe(
			map(locs => (locs ? locs[key] : null)),
			map<Localization | null, R>(loc => (!mapToValue ? loc : loc?.value || null) as R)
		);
	}

	private mapLocalizationItem = ([name, loc]: [string, LocDef]): CmLocalizationItem => ({
		name,
		type: this.mapLocType(loc),
	});

	private fetchClientLocalizations$(
		localizations: CmLocalizationItem[],
		culture?: string,
		fetchPolicy?: WatchQueryFetchPolicy
	): Observable<ClientLocalization[]> {
		// Avoid the network call if there are no locs
		if (!localizations.length) {
			return of([]);
		}
		const culture$ = culture ? of(culture) : this.settings.cultureCode$;
		return combineLatest([culture$, this.settings.client$]).pipe(
			switchMap(
				([language, clientDbAlias]) =>
					this.apollo.watchQuery<
						{ clientLocalizationList: ClientLocalization[] },
						{ request: ClientLocalizationRequest }
					>({
						query: CLIENT_LOCALIZATIONS,
						variables: {
							request: {
								clientDbAlias,
								language,
								localizations,
							},
						},
						fetchPolicy: fetchPolicy || 'cache-first',
					}).valueChanges
			),
			catchError(() => of({ data: { clientLocalizationList: [] } })),
			map(({ data }) => data.clientLocalizationList || [])
		);
	}

	private fetchLocCultureValue$(
		name: string,
		type?: LocType,
		culture?: string
	): Observable<string> {
		if (!type) {
			type = this.getPortalType();
		}

		return this.fetchClientLocalizations$([{ name, type }], culture).pipe(
			map(locs => (locs.length ? locs[0] : null)),
			map(loc => loc?.value || '')
		);
	}

	private mapLocType({ type, portal }: Pick<LocDef, 'type' | 'portal'>): LocType {
		if (portal === 'SP') {
			return LocType.SponsorPortalString;
		}
		if (portal === 'CP') {
			return LocType.CandidatePortalString;
		}
		return type ? type : this.getPortalType();
	}

	private getPortalType(): LocType {
		return this.settings.portal === PortalType.SponsorPortal
			? LocType.SponsorPortalString
			: LocType.CandidatePortalString;
	}
}
